import { useEffect, useRef, useMemo } from 'react';
import { createChart, LineSeries } from 'lightweight-charts';
import { Radio, Spin } from 'antd';
import { darkTheme, lightTheme, timeToLocal } from '@/utils/helper';
import { RESOLUTION_MAP } from './multi-strike-utils';

const MultiStrikeChart = ({
  mode,
  chartData,
  chartLoading,
  isDark,
  oiView,
  showIndividual,
  showCombined,
  showAtp,
  onChangeOiView,
  strikeTokenMap,
  selectedStrikes,
  isLiveFeedActive,
  isMarketOpen,
  resolution,
}) => {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const seriesMapRef = useRef({});
  const metaRef = useRef({});
  const resizeObserverRef = useRef(null);

  // Create chart once
  useEffect(() => {
    if (!containerRef.current) return;

    chartRef.current = createChart(containerRef.current, {
      width: containerRef.current.clientWidth,
      height: containerRef.current.clientHeight,
      rightPriceScale: {
        visible: true,
        scaleMargins: { top: 0.1, bottom: 0.1 },
      },
      leftPriceScale: { visible: mode === 'lp' && showCombined },
      layout: isDark ? darkTheme.layout : lightTheme.layout,
      grid: { horzLines: { visible: false }, vertLines: { visible: false } },
      timeScale: {
        borderVisible: true,
        timeVisible: true,
        secondsVisible: false,
        rightOffset: 2,
        barSpacing: 3,
      },
      crosshair: { mode: 0 },
    });

    // Resize observer
    resizeObserverRef.current = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      chartRef.current?.applyOptions({ width, height });
      chartRef.current?.timeScale().fitContent();
    });
    resizeObserverRef.current.observe(containerRef.current);

    return () => {
      resizeObserverRef.current?.disconnect();
      chartRef.current?.remove();
      chartRef.current = null;
      seriesMapRef.current = {};
    };
  }, []);

  // Theme updates
  useEffect(() => {
    if (!chartRef.current) return;
    chartRef.current.applyOptions(isDark ? darkTheme : lightTheme);
  }, [isDark]);

  // Set chart data
  useEffect(() => {
    if (!chartRef.current || !chartData) return;

    // Clear existing series
    Object.values(seriesMapRef.current).forEach((s) => {
      try { chartRef.current.removeSeries(s); } catch { }
    });
    seriesMapRef.current = {};
    metaRef.current = {};

    const { seriesMap, combinedPoints, combinedAtpPoints } = chartData;
    const newSeriesMap = {};
    const meta = {};

    const isOiMode = mode === 'oi';
    const useOiChange = isOiMode && oiView === 'oic';
    const formatOi = isOiMode ? {
      type: 'custom',
      formatter: (price) => `${(price / 100000).toFixed(2)}L`,
    } : {};

    // Individual series
    if (seriesMap) {
      Object.entries(seriesMap).forEach(([key, info]) => {
        const visible = showIndividual && !useOiChange;
        const lineSeries = chartRef.current.addSeries(LineSeries, {
          color: info.color,
          lineWidth: 2,
          visible,
          priceScaleId: 'right',
          priceFormat: formatOi,
        });

        const data = useOiChange && info.oicPoints?.length
          ? info.oicPoints
          : info.points;

        if (data?.length) {
          lineSeries.setData(data.sort((a, b) => a.time - b.time));
        }

        newSeriesMap[key] = lineSeries;
        meta[key] = { color: info.color, text: info.label };
      });

      // OI Change series (separate)
      if (useOiChange) {
        Object.entries(seriesMap).forEach(([key, info]) => {
          if (info.oicPoints?.length) {
            const oicSeries = chartRef.current.addSeries(LineSeries, {
              color: info.color,
              lineWidth: 2,
              visible: true,
              priceScaleId: 'right',
              priceFormat: formatOi,
            });
            oicSeries.setData(info.oicPoints.sort((a, b) => a.time - b.time));
            newSeriesMap[`${key}_oic`] = oicSeries;
            meta[`${key}_oic`] = { color: info.color, text: `${info.label} Chg` };
          }
        });
      }
    }

    // Combined series (LP mode)
    if (combinedPoints?.length) {
      const combinedSeries = chartRef.current.addSeries(LineSeries, {
        color: isDark ? '#e2e8f0' : '#1e293b',
        lineWidth: 2,
        priceScaleId: 'left',
      });
      combinedSeries.setData(combinedPoints);
      newSeriesMap.__combined = combinedSeries;
      meta.__combined = { color: isDark ? '#e2e8f0' : '#1e293b', text: 'Combined' };
    }

    if (combinedAtpPoints?.length) {
      const atpSeries = chartRef.current.addSeries(LineSeries, {
        color: '#ffa500',
        lineWidth: 2,
        lineStyle: 1,
        priceScaleId: 'left',
      });
      atpSeries.setData(combinedAtpPoints);
      newSeriesMap.__atp = atpSeries;
      meta.__atp = { color: '#ffa500', text: 'ATP' };
    }

    // Left scale visibility
    chartRef.current.applyOptions({
      leftPriceScale: { visible: !!combinedPoints?.length },
    });

    seriesMapRef.current = newSeriesMap;
    metaRef.current = meta;
    chartRef.current.timeScale().fitContent();

  }, [chartData, oiView, showIndividual, showCombined, showAtp, isDark, mode]);

  return (
    <div className="ms-chart-container">
      {mode === 'oi' && (
        <div className="ms-oi-toggle">
          <Radio.Group
            size="small"
            buttonStyle="solid"
            value={oiView}
            onChange={(e) => onChangeOiView(e.target.value)}
          >
            <Radio.Button value="oi">OI</Radio.Button>
            <Radio.Button value="oic">OI CHANGE</Radio.Button>
          </Radio.Group>
        </div>
      )}

      <div className="ms-chart-wrapper" ref={containerRef}>
        {chartLoading && (
          <div className="ms-chart-loading">
            <Spin />
          </div>
        )}
      </div>
    </div>
  );
};

export default MultiStrikeChart;
